import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { BaseSettingsContainerState } from "@/Common/Components/Settings/BaseSettingsContainer";
import { SearchSettingsFooter } from "@/Common/Components/Settings/SearchSettingsFooter";
import { PortfolioInboxes } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { CheckboxLabel } from "@/DesignSystem";
import { ISentFaxInboxModel } from "@/Portfolio/Models/UI/ISentFaxInboxModel";

import "./PortfolioFaxSettingsContainer.css";

interface IPortfolioFaxSettingsContainerProps {
    model: ISentFaxInboxModel;
    onClick?: any;
    id?: string;
    currentInbox: PortfolioInboxes;
    className?: string;
    style?: any;
}

class PortfolioFaxSettingsContainerState extends BaseSettingsContainerState {
    public constructor(fields?: Partial<PortfolioFaxSettingsContainerState>) {
        super();
        Object.assign(this, fields);
    }

    public static getDefault(): PortfolioFaxSettingsContainerState {
        return new PortfolioFaxSettingsContainerState({
            isSaveSettingsChecked: false
        });
    }
}

class PortfolioFaxSettingsContainer extends BaseReactComponent<IPortfolioFaxSettingsContainerProps, PortfolioFaxSettingsContainerState> {
    private readonly sitesCheckboxId: string = "sites_check";
    private readonly cliniciansCheckboxId: string = "clinicians_check";
    private readonly statusesCheckboxId: string = "status_check";

    public constructor(props: IPortfolioFaxSettingsContainerProps) {
        super(props);
        this.state = PortfolioFaxSettingsContainerState.getDefault();
    }

    public componentDidMount(): void {
        super.setOverlay("MySentFaxSettingsContainer", {
            transparent: true,
            onClicked: () => this.onOkClick()
        });
    }

    public render(): JSX.Element {
        const settingsclass = `${this.props.className} portfolio-settings`;

        return (
            <div
                id={this.props.id}
                className={settingsclass}
                style={this.props.style}
                onClick={() => this.props.onClick?.()}
            >
                <table className="f-content">
                    <thead>
                        <tr>
                            <th>
                                <label className="date-label">{cacheStore.GetString("DateLabel").toUpperCase()}</label>
                            </th>
                            {this.props.model.get("Sites").count() > 1 && (
                                <th>
                                    <CheckboxLabel
                                        id={this.sitesCheckboxId}
                                        label={cacheStore.GetString("SitesLabel").toUpperCase()}
                                        checked={this.props.model.get("SelectAllSitesChecked")}
                                        indeterminate={this.props.model.get("SelectAllSitesIndeterminate")}
                                        onChange={checked => {
                                            this.onCheckboxChange(
                                                LocalOperations.Portfolio.MySentFaxes_OnSettingsContainerCheckboxChange,
                                                checked,
                                                this.sitesCheckboxId,
                                                CheckboxTarget.Myle_Portfolio_MySentFaxes_SitesControlCheck,
                                                true
                                            );
                                        }}
                                    />
                                </th>
                            )}
                            <th>
                                <CheckboxLabel
                                    id={this.cliniciansCheckboxId}
                                    label={cacheStore.GetString("CliniciansLabel").toUpperCase()}
                                    checked={this.props.model.get("SelectAllCliniciansChecked")}
                                    indeterminate={this.props.model.get("SelectAllCliniciansIndeterminate")}
                                    onChange={checked => {
                                        this.onCheckboxChange(
                                            LocalOperations.Portfolio.MySentFaxes_OnSettingsContainerCheckboxChange,
                                            checked,
                                            this.cliniciansCheckboxId,
                                            CheckboxTarget.Myle_Portfolio_MySentFaxes_CliniciansControlCheck,
                                            true
                                        );
                                    }}
                                />
                            </th>
                            <th>
                                <CheckboxLabel
                                    id={this.statusesCheckboxId}
                                    label={cacheStore.GetString("StatusLabel").toUpperCase()}
                                    checked={this.props.model.get("SelectAllStatusChecked")}
                                    indeterminate={this.props.model.get("SelectAllStatusIndeterminate")}
                                    onChange={checked => {
                                        this.onCheckboxChange(
                                            LocalOperations.Portfolio.MySentFaxes_OnSettingsContainerCheckboxChange,
                                            checked,
                                            this.statusesCheckboxId,
                                            CheckboxTarget.Myle_Portfolio_MySentFaxes_Status,
                                            true
                                        );
                                    }}
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <MultiDateSelector
                                    id="doc_search_date_selector"
                                    fromMyleOperation={LocalOperations.Portfolio.MySentFaxes_SearchSettings_FromDateChanged}
                                    toMyleOperation={LocalOperations.Portfolio.MySentFaxes_SearchSettings_ToDateChanged}
                                    fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                    toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                                    from={this.props.model.get("DateFrom")}
                                    to={this.props.model.get("DateTo")}
                                />
                            </td>
                            {this.props.model.get("Sites").count() > 1 && (
                                <td className="sites-column">
                                    <div className="sites-control">
                                        <CheckedListControl
                                            DataSource={this.props.model.get("Sites")}
                                            MaxTextLength={25}
                                            onChange={(checked, checkboxId) => {
                                                this.onCheckboxChange(
                                                    LocalOperations.Portfolio.MySentFaxes_OnSettingsContainerCheckboxChange,
                                                    checked,
                                                    checkboxId,
                                                    CheckboxTarget.Myle_Portfolio_MySentFaxes_SitesControlCheck
                                                );
                                            }}
                                        />
                                        <CheckboxControl
                                            id="sites_onlySelected"
                                            label={cacheStore.GetString("ShowOnlySelectedSiteLabel").toUpperCase()}
                                            operation={LocalOperations.Portfolio.MySentFaxes_OnSettingsContainerCheckboxChange}
                                            checked={this.props.model.get("ShowOnlySelectedSites")}
                                            target={CheckboxTarget.Myle_Portfolio_MySentFaxes_OnlySelectedSites}
                                            DivWrap
                                        />
                                    </div>
                                </td>
                            )}
                            <td>
                                <input
                                    className="form-control clinicsearch-text"
                                    type="text"
                                    value={this.props.model.get("CliniciansSearchText")}
                                    onChange={(e: any) => this.onCliniciansFilterTextChanged(e.target.value)}
                                    placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                                />
                                <div className="clinician-filter">
                                    <CheckedListControl
                                        DataSource={this.props.model.get("CliniciansFiltered")}
                                        sort
                                        limitTextWidth
                                        enableLazyRendering
                                        onChange={(checked, checkboxId) => {
                                            this.onCheckboxChange(
                                                LocalOperations.Portfolio.MySentFaxes_OnSettingsContainerCheckboxChange,
                                                checked,
                                                checkboxId,
                                                CheckboxTarget.Myle_Portfolio_MySentFaxes_CliniciansControlCheck
                                            );
                                        }}
                                    />
                                </div>
                            </td>
                            <td>
                                <CheckedListControl
                                    DataSource={this.props.model.get("Status")}
                                    onChange={(checked, checkboxId) => {
                                        this.onCheckboxChange(
                                            LocalOperations.Portfolio.MySentFaxes_OnSettingsContainerCheckboxChange,
                                            checked,
                                            checkboxId,
                                            CheckboxTarget.Myle_Portfolio_MySentFaxes_Status
                                        );
                                    }}
                                />
                                {this.props.currentInbox === PortfolioInboxes.FailedFaxes && (
                                    <CheckboxControl
                                        id="includeIgnored-check"
                                        label={cacheStore.GetString("IncludeIgnoredFaxes")}
                                        operation={LocalOperations.Portfolio.MySentFaxes_OnSettingsContainerCheckboxChange}
                                        checked={this.props.model.get("IncludeIgnored")}
                                        target={CheckboxTarget.Myle_Portfolio_MySentFaxes_IncludeIgnoredCheckboxChange}
                                    />
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
                <SearchSettingsFooter
                    onClose={() => sessionStore.dispatch(LocalOperations.Portfolio.MySentFaxes_SearchSettings_CancelClick)}
                    onOk={() => this.onOkClick()}
                    onColumnOrderingButtonClick={() => sessionStore.dispatch(LocalOperations.Portfolio.MySentFaxes_SearchSettings_ColumnOrderingPopup_Open)}
                    onResultsPerPageChange={value => sessionStore.dispatch(LocalOperations.Portfolio.MySentFaxes_SearchSettings_OnResultsPerPageChange, { value })}
                    selectedPageSizeOption={this.props.model.get("SelectedPageSizeOption")}
                    pageSizeOptions={this.props.model.get("PageSizeOptions").toArray()}
                    onSaveSettingsCheckboxChange={checked => this.mutateState(s => s.isSaveSettingsChecked = checked)}
                    isSaveSettingsChecked={this.state.isSaveSettingsChecked}
                />
            </div>
        );
    }

    public onCliniciansFilterTextChanged(value: string): void {
        sessionStore.dispatch(LocalOperations.Portfolio.MySentFaxes_SearchSettings_ClinicianFilterTextChanged, { value });
    }

    private onOkClick(): void {
        sessionStore.dispatch(LocalOperations.Portfolio.MySentFaxes_SearchSettings_OkClick, { isSaveSettingsChecked: this.state.isSaveSettingsChecked });
    }

    private onCheckboxChange(operation: number, checked: boolean, checkboxId: string, target: CheckboxTarget, isParent: boolean = false): void {
        sessionStore.dispatch(
            operation,
            {
                target,
                checked,
                id: checkboxId,
                isParent
            }
        );
    }
}

export { PortfolioFaxSettingsContainer };
